<script>
  import { onMount, createEventDispatcher } from "svelte";
  import { log, announce } from "./Logging.svelte";
  const dispatch = createEventDispatcher();
  export let prefs = {};
  export let auth = null; // passed by App.svelte; unused here
  export let firebase = null;
  // Name of the friend whose invite link this account just accepted.
  export let invitedBy = null;

  $: greetingName = (prefs && (prefs.name || prefs.email)) || "";

  onMount(() => {
    announce("Welcome Mounted");
    return () => {};
  });
  function closeConfig() {
    dispatch("modeChange", { action: "window" });
  }
  function openConfig() {
    dispatch("openConfig", { action: "" });
  }
  function openFriendList() {
    dispatch("openFriendList", { action: "" });
  }
</script>

<div class="home">
  <header class="home__top">
    <div class="home__bar">
      <a class="home__brand" href="https://passcall.io/" aria-label="PassCall site">
        <img src="/images/PC-website-hd-logo.png" alt="PassCall" width="299" height="108" />
      </a>
      <nav class="home__nav" aria-label="Account">
        <button type="button" on:click={openFriendList}>Friends</button>
        <button type="button" on:click={openConfig}>Settings</button>
      </nav>
    </div>
  </header>

  <main class="home__main">
    <p class="home__eyebrow">{greetingName ? `Signed in as ${greetingName}` : "Signed in"}</p>
    <h1 class="home__title">Ready when <em>you</em> are.</h1>
    <p class="home__lede">Open the window and leave it on. Whenever you and a friend walk past your screens at the same time, you'll see each other.</p>
    {#if invitedBy}
      <p class="home__note" role="status"><strong>{invitedBy}</strong> is on your friends list now, and you're on theirs. Open the window, and the next time you both walk by, you'll see each other.</p>
    {/if}

    <div class="home__actions">
      <button type="button" class="home__card home__card--primary" on:click|once={closeConfig}>
        <span class="home__card-title">Open the window</span>
        <span class="home__card-text">Turns on the camera and goes online. Best in a room you walk through.</span>
      </button>
      <button type="button" class="home__card" on:click={openFriendList}>
        <span class="home__card-title">Friends</span>
        <span class="home__card-text">Choose who can appear in your window.</span>
      </button>
      <button type="button" class="home__card" on:click={openConfig}>
        <span class="home__card-title">Settings</span>
        <span class="home__card-text">Camera, microphone, do-not-disturb and your screen background.</span>
      </button>
      <button type="button" class="home__card" on:click={openFriendList}>
        <span class="home__card-title">Invite someone</span>
        <span class="home__card-text">Send your link. Whoever sets up PassCall with it is your friend at once.</span>
      </button>
    </div>
  </main>

  <footer class="home__foot">
    <span>&copy; 2026 PassCall. All rights reserved.</span>
    <a href="mailto:help@passcall.io">help@passcall.io</a>
  </footer>
</div>

<style>
  .home { min-height: 100vh; display: flex; flex-direction: column; font-family: var(--font); color: var(--on-dark);
    background: radial-gradient(70% 55% at 10% 0%, rgba(31, 107, 63, .58), transparent 65%), radial-gradient(45% 45% at 92% 70%, rgba(212, 175, 55, .2), transparent 65%), var(--ink); }
  .home__top { background: var(--wood); border-bottom: 2px solid var(--wood-trim); box-shadow: 0 6px 18px rgba(0, 0, 0, .35); }
  .home__bar { max-width: 1200px; margin: 0 auto; padding: 0 16px; min-height: 68px; display: flex; align-items: center; gap: 1.5rem; }
  .home__brand img { height: 34px; width: auto; filter: brightness(0) invert(1); display: block; }
  .home__nav { margin-left: auto; display: flex; gap: .5rem; flex-wrap: wrap; }
  .home__nav button { font: inherit; font-weight: 500; font-size: .95rem; padding: .5rem .9rem; border-radius: 999px; border: 1.5px solid rgba(244, 237, 227, .35); background: transparent; color: var(--on-dark); cursor: pointer; }
  .home__nav button:hover { background: var(--on-dark); color: var(--ink); }

  .home__main { flex: 1; max-width: 1200px; width: 100%; margin: 0 auto; padding: clamp(3rem, 8vw, 6rem) 16px; }
  .home__eyebrow { margin: 0 0 1rem; font-size: .78rem; font-weight: 600; letter-spacing: .14em; text-transform: uppercase; color: var(--amber); display: inline-flex; align-items: center; gap: .5rem; }
  .home__eyebrow::before { content: ""; width: 8px; height: 8px; border-radius: 50%; background: currentColor; }
  .home__title { font-family: var(--display); font-weight: 500; font-size: clamp(2.6rem, 5vw + .5rem, 4.5rem); line-height: 1; letter-spacing: -.025em; margin: 0 0 1rem; max-width: 14ch; }
  .home__title em { background: var(--gold-metal); -webkit-background-clip: text; background-clip: text; color: transparent; font-style: italic; }
  .home__lede { margin: 0 0 2.5rem; max-width: 46ch; font-size: 1.15rem; line-height: 1.5; color: var(--on-dark-muted); }

  .home__actions { display: grid; gap: 1rem; }
  @media (min-width: 720px) { .home__actions { grid-template-columns: 1fr 1fr; } }
  @media (min-width: 1000px) { .home__actions { grid-template-columns: 1.4fr 1fr 1fr 1fr; } }
  .home__note { margin: -1.5rem 0 2.5rem; max-width: 52ch; padding: .9rem 1.1rem; border-radius: 12px; background: rgba(212, 175, 55, .16); border: 1px solid rgba(212, 175, 55, .45); color: var(--on-dark); font-size: 1.05rem; line-height: 1.45; }
  .home__card { font: inherit; text-align: left; display: flex; flex-direction: column; gap: .5rem; padding: 1.5rem; border-radius: 18px; border: 1px solid var(--line-dark); background: rgba(244, 237, 227, .05); color: var(--on-dark); cursor: pointer; transition: background .15s, transform .15s; }
  .home__card:hover { background: rgba(244, 237, 227, .1); transform: translateY(-2px); }
  .home__card--primary { background: var(--gold-metal); border-color: transparent; }
  .home__card--primary, .home__card--primary * { color: var(--ink); }
  .home__card--primary:hover { background: var(--gold-metal); filter: brightness(1.08); }
  .home__card-title { font-family: var(--display); font-size: 1.6rem; line-height: 1.1; }
  .home__card-text { font-size: .95rem; color: var(--on-dark-muted); }
  .home__card--primary .home__card-text { color: rgba(15, 42, 30, .8); }

  .home__foot { border-top: 1px solid var(--line-dark); color: var(--on-dark-muted); font-size: .875rem; padding: 1.25rem 16px; display: flex; flex-wrap: wrap; justify-content: center; gap: .5rem 2rem; }
  .home__foot a { color: var(--on-dark); text-decoration: none; }
</style>
